import React, { useCallback, useEffect, useMemo } from 'react';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { NON_WORKING_DAYS_QUERY, SITES_QUERY } from '../queries';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import NonWorkingDayComponent from './NonWorkingDayComponent';
import NonWorkingDayModalContainer from 'features/nonWorkingDays/edit/components/NonWorkingDayModalContainer';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { nonWorkingDaysStore } from '../nonWorkingDaysStore';
import { sortBy } from 'utils/functions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { NON_WORKING_DAY_DELETED } from '../subscriptions';
import useSubscription from 'hooks/apollo/useSubscription';
import { CollectionType, useListBrowserUtils } from '@ts-private/list-browser';
import useQuery from 'hooks/apollo/useQuery';
import { format } from 'date-fns';
import { isNullish } from '@apollo/client/cache/inmemory/helpers';

const filterNonWorkingDays = (data, pager, filters) => {
  let nonWorkingDays = [...data];

  if (filters.search) {
    nonWorkingDays = nonWorkingDays.filter(item => {
      return item.description && item.description.toLowerCase().trim().includes(filters.search.toLowerCase().trim());
    });
  }

  if (filters.nonWorkingDayDate)
    nonWorkingDays = nonWorkingDays.filter(item => {
      if (!item.nonWorkingDayDate) return false;
      const dateValue = item.nonWorkingDayDate.split('T')[0];
      if (format(new Date(dateValue), 'yyyy-MM-dd') !== filters.nonWorkingDayDate) return false;
      return true;
    });

  if (filters.description)
    nonWorkingDays = nonWorkingDays.filter(item => {
      return item.description && item.description.toLowerCase().trim().includes(filters.description.toLowerCase().trim());
    });

  if (!isNullish(filters.workingSiteId)) {
    nonWorkingDays = nonWorkingDays.filter(item => {
      return !isNullish(item.workingSiteId) && item.workingSiteId === filters.workingSiteId;
    });
  }

  const orderBy = [];
  if (pager.orderBy) orderBy.push(pager.orderBy);
  else orderBy.push('nonWorkingDayDate');

  sortBy(nonWorkingDays, orderBy, pager.direction);
  return nonWorkingDays;
};

const NonWorkingDaysListBrowser = () => {
  const addToast = useToast();
  const { t } = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: NON_WORKING_DAYS_QUERY,
        name: 'nonWorkingDays'
      },
      clientFilter: filterNonWorkingDays
    }),
    []
  );

  const columns = useMemo(
    () => [
      { name: 'nonWorkingDayDate', caption: 'NonWorkingDays.Date' },
      { name: 'description', caption: 'NonWorkingDays.Description' },
      { name: 'workingSiteId', caption: 'NonWorkingDays.WorkingSite' },
      { name: 'workingDay', caption: 'NonWorkingDays.WorkingDay', align: 'center' },
      { name: 'validInAllContexts', caption: 'NonWorkingDays.ValidInAllContexts', align: 'center' },
      { name: 'contexts', caption: 'NonWorkingDays.Contexts', width: 35 },
      { name: 'actions', caption: 'Actions', width: 10 }
    ],
    []
  );

  useSubscription(NON_WORKING_DAY_DELETED, {
    onData: ({ client }) => {
      addToast(t('NonWorkingDays.NonWorkingDayDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'nonWorkingDays' });
      client.cache.gc();
    }
  });

  const itemComponent = useCallback(({ data }) => <NonWorkingDayComponent data={data} />, []);

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='non-working-days-browser' store={nonWorkingDaysStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={nonWorkingDaysStore} options={fetchingOptions} />
      <NonWorkingDayModalContainer />
      <NonWorkingDayFiltersLoader />
      <ApolloFetchingComponent store={nonWorkingDaysStore} options={fetchingOptions} />
    </Card>
  );
};

export default NonWorkingDaysListBrowser;

const NonWorkingDayFiltersLoader = () => {
  const { t } = useTranslation();
  const { updateCollection } = useListBrowserUtils(nonWorkingDaysStore);

  const { data } = useQuery(SITES_QUERY);
  const allSites = useMemo(
    () =>
      [...(data?.sites || []), { siteId: -1, siteName: t('NonWorkingDays.AllSites') }].map(({ siteId, siteName }) => ({
        value: siteId,
        label: siteName
      })),
    [data, t]
  );

  useEffect(() => {
    updateCollection(allSites, 'workingSiteId', CollectionType.Filter);
  }, [updateCollection, allSites]);
};
